import { localizer } from "@stormglass/game-localization";
import { Link, useNavigate } from "react-router-dom";

import { RecoveryBanner } from "../components/RecoveryBanner";
import { WeatherIcon } from "../components/WeatherIcon";
import { useGameHost, useGameState } from "../game/GameContext";

const supportsWebGl = (): boolean => {
  const canvas = document.createElement("canvas");
  return (
    canvas.getContext("webgl2") !== null || canvas.getContext("webgl") !== null
  );
};

export function HomeScreen(): React.JSX.Element {
  const host = useGameHost();
  const { ui, snapshot } = useGameState();
  const navigate = useNavigate();
  const hasRun = ui.phase !== "idle" && ui.phase !== "ended";
  const capabilities = {
    indexedDb: "indexedDB" in globalThis,
    webGl: supportsWebGl(),
  };
  const begin = async (): Promise<void> => {
    await host.unlockAudio();
    if (!hasRun) host.startFreshRun();
    void navigate("/play");
  };
  return (
    <div className="home-page">
      <RecoveryBanner />
      {(!capabilities.indexedDb || !capabilities.webGl) && (
        <div className="capability-warning" role="alert">
          <strong>Limited device support</strong>
          <p>
            {!capabilities.webGl
              ? "WebGL is unavailable, so the battlefield may not render."
              : "Local saves are unavailable in this browser mode."}
          </p>
        </div>
      )}
      <section className="hero">
        <div className="hero__copy">
          <p className="eyebrow">{localizer.t("home.eyebrow")}</p>
          <h1>
            Hold the <em>eye</em>
            <br />
            of the storm.
          </h1>
          <p className="hero__lede">{localizer.t("home.lede")}</p>
          <div className="hero__actions">
            <button
              className="button button--primary button--large"
              type="button"
              onClick={() => void begin()}
            >
              <span>
                {hasRun
                  ? localizer.t("action.continue")
                  : localizer.t("action.play")}
              </span>
              <b>→</b>
            </button>
            <Link className="button button--ghost" to="/shop">
              Open chamber lab
            </Link>
          </div>
          <div className="hero__proof">
            <span>
              <i>✓</i>
              {localizer.t("home.offline")}
            </span>
            <span>
              <i>◇</i>
              {localizer.t("home.local")}
            </span>
          </div>
        </div>
        <div className="hero-chamber" aria-hidden="true">
          <div className="hero-chamber__orbit hero-chamber__orbit--outer">
            <i />
            <i />
            <i />
            <i />
          </div>
          <div className="hero-chamber__orbit hero-chamber__orbit--inner" />
          <div className="hero-chamber__core">
            <span>ϟ</span>
          </div>
          <div className="hero-chamber__beam" />
          <span className="hero-chamber__label hero-chamber__label--one">
            INTEGRITY // 100%
          </span>
          <span className="hero-chamber__label hero-chamber__label--two">
            ARRAY // STABLE
          </span>
        </div>
      </section>
      <section className="system-strip" aria-label="Weather systems">
        <div>
          <small>ACTIVE ARRAY</small>
          <strong>
            <WeatherIcon kind="lightning" />
            Priority Lightning
          </strong>
          <span>Targets the nearest threat</span>
        </div>
        <div>
          <small>OWNED SYSTEMS</small>
          <strong>
            {snapshot.ownedWeapons.length.toString().padStart(2, "0")}
          </strong>
          <span>All join every run</span>
        </div>
        <div>
          <small>BEST ROUND</small>
          <strong>
            {Math.max(0, host.getStatistics().highestRound)
              .toString()
              .padStart(2, "0")}
          </strong>
          <span>Atmospheric record</span>
        </div>
        <div className="system-strip__weather">
          {(["rain", "wind", "hail"] as const).map((kind) => (
            <span
              className={snapshot.ownedWeapons.includes(kind) ? "is-owned" : ""}
              key={kind}
            >
              <WeatherIcon kind={kind} />
              <small>
                {snapshot.ownedWeapons.includes(kind) ? "ONLINE" : "LOCKED"}
              </small>
            </span>
          ))}
        </div>
      </section>
    </div>
  );
}
